/**
 * File: contractImportRows.ts
 * Path: packages/config/src/contracts/contractImportRows.ts
 * Description: Bringing in several old signed contracts at once. A CSV has one
 * row per contract; the old signed PDFs are picked alongside it and matched
 * to the rows by file name. Everything here is checking and reading: the
 * admin shows a preview with what is wrong on each row, and nothing is saved
 * until the person presses the button.
 *
 * Dates are YYYY-MM-DD or DD/MM/YYYY. Amounts are plain rand ("12500",
 * "R12 500,50"). `covers` and `phases` separate items with a semicolon:
 * "Design, 5000; Build, 7000".
 */

import { parseCsv } from "../accounting/csv";
import { PAYMENT_PLAN_ORDER } from "../pricing/paymentConstants";
import type { PaymentPlanId } from "../pricing/pricingTypes";
import { southAfricanDay } from "./contractDue";
import { contractImportIssues, parseRand, parseStages } from "./contractImport";
import { CONTRACT_IMPORT_SOURCES, emptyContractContent } from "./contractRecord";
import type { ContractContent, ContractImportSource, RetainerBilling } from "./contractTypes";

export const IMPORT_ROW_HEADERS = [
  "client",
  "job",
  "type",
  "covers",
  "fee",
  "plan",
  "phases",
  "monthly_fee",
  "term_months",
  "paid",
  "start_date",
  "signed_on",
  "source",
  "client_signed_by",
  "idk_signed_by",
  "pdf",
  "notes",
] as const;

type Header = (typeof IMPORT_ROW_HEADERS)[number];

/** A file to fill in, with one example row that shows each column. */
export const importTemplateCsv = (): string =>
  [
    IMPORT_ROW_HEADERS.join(","),
    [
      "Pondo Tours",
      "Pondo website",
      "service",
      '"Website, five pages; Google Maps listing"',
      "12500",
      "split_50_50",
      "",
      "",
      "",
      "",
      "2025-03-01",
      "2025-02-27",
      "zoho",
      "Sipho Dlamini",
      "Liam Daries",
      "pondo-website-contract.pdf",
      "",
    ].join(","),
    [
      "Pondo Tours",
      "Care plan",
      "retainer",
      "Website care plan",
      "",
      "",
      "",
      "1250",
      "12",
      "monthly",
      "2025-04-01",
      "2025-03-28",
      "google_docs",
      "Sipho Dlamini",
      "Liam Daries",
      "pondo-care-plan.pdf",
      "",
    ].join(","),
  ].join("\n");

export interface ImportRowDraft {
  /** The line in the CSV file (the header is line 1). */
  line: number;
  clientName: string;
  jobName: string;
  pdfName: string;
  content: ContractContent;
  signedOn: string;
  source: ContractImportSource;
  clientSignerName: string;
  providerSignerName: string;
  notes: string;
  /** What can't be read or is missing, in plain words; empty when the row reads fine. */
  problems: string[];
}

const normaliseHeader = (h: string): string => h.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

/** A date as YYYY-MM-DD from "2026-02-27" or "27/02/2026"; "" when it isn't one. */
export const readImportDate = (text: string): string => {
  const t = text.trim();
  let y = 0;
  let m = 0;
  let d = 0;
  let match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (match) [y, m, d] = [+match[1], +match[2], +match[3]];
  else if ((match = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/.exec(t))) [y, m, d] = [+match[3], +match[2], +match[1]];
  else return "";
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (y < 2000 || y > 2100 || m < 1 || m > 12 || d < 1 || d > last) return "";
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

const PLAN_ALIASES: Record<string, PaymentPlanId> = {
  once_off: "once_off",
  upfront: "once_off",
  split_50_50: "split_50_50",
  "50_50": "split_50_50",
  phase_by_phase: "phase_by_phase",
  phases: "phase_by_phase",
  custom: "phase_by_phase",
  custom_amounts: "phase_by_phase",
  split_6_month: "split_6_month",
  "6_month": "split_6_month",
  split_12_month: "split_12_month",
  "12_month": "split_12_month",
};

const SOURCE_ALIASES: Record<string, ContractImportSource> = {
  zoho: "zoho",
  zoho_contracts: "zoho",
  google_docs: "google_docs",
  google: "google_docs",
  other: "other",
};

const SOURCES = new Set<string>(CONTRACT_IMPORT_SOURCES);
const PLANS = new Set<string>(PAYMENT_PLAN_ORDER);

export interface ReadImportRows {
  /** Why the whole file can't be used (no rows, a column missing); "" when it reads. */
  fileProblem: string;
  rows: ImportRowDraft[];
}

export const readImportRows = (csv: string, nowMs: number, defaultProvider: string): ReadImportRows => {
  const table = parseCsv(csv, true);
  if (table.every((r) => r.every((c) => !c.trim()))) return { fileProblem: "That file is empty.", rows: [] };
  const columns = table[0].map(normaliseHeader);
  const at = (h: Header): number => columns.indexOf(h);
  const missing = (["client", "type", "signed_on", "pdf"] as Header[]).filter((h) => at(h) < 0);
  if (missing.length > 0) {
    return {
      fileProblem: `The file has no ${missing.map((m) => `"${m}"`).join(", ")} column. Use the template: it has every column.`,
      rows: [],
    };
  }

  const today = southAfricanDay(nowMs);
  const rows: ImportRowDraft[] = [];
  table.slice(1).forEach((cells, index) => {
    if (cells.every((c) => !c.trim())) return;
    const get = (h: Header): string => (at(h) < 0 ? "" : (cells[at(h)] ?? "").trim());
    const problems: string[] = [];

    const typeText = get("type").toLowerCase();
    const template = typeText === "service" || typeText === "retainer" ? typeText : null;
    if (!template) problems.push('"type" must be service or retainer.');
    const content = emptyContractContent(template ?? "service", today);

    const signedOn = readImportDate(get("signed_on"));
    if (!signedOn) problems.push('"signed_on" needs a date like 2026-02-27 or 27/02/2026.');
    const startText = get("start_date");
    const startDate = startText ? readImportDate(startText) : signedOn;
    if (startText && !startDate) problems.push('"start_date" needs a date like 2026-03-01 or 01/03/2026.');

    const plan = PLAN_ALIASES[normaliseHeader(get("plan"))] ?? "once_off";
    if (get("plan") && !PLAN_ALIASES[normaliseHeader(get("plan"))]) {
      problems.push(`"plan" ${get("plan")} isn't one of ${[...PLANS].join(", ")}.`);
    }
    const source = SOURCE_ALIASES[normaliseHeader(get("source"))] ?? "other";
    if (get("source") && !SOURCES.has(source)) problems.push('"source" must be zoho, google_docs or other.');
    const paid: RetainerBilling = /once|up ?front|term/i.test(get("paid")) ? "once_off" : "monthly";
    const term = get("term_months") ? Math.round(Number(get("term_months"))) : content.minimumTermMonths;

    const draft: ImportRowDraft = {
      line: index + 2,
      clientName: get("client"),
      jobName: get("job"),
      pdfName: get("pdf"),
      signedOn,
      source,
      clientSignerName: get("client_signed_by"),
      providerSignerName: get("idk_signed_by") || defaultProvider,
      notes: get("notes"),
      problems,
      content: {
        ...content,
        client: { ...content.client, name: get("client"), signatoryName: get("client_signed_by") },
        scopeItems: get("covers")
          .split(/;|\n/)
          .map((s) => s.trim())
          .filter(Boolean),
        commencementDate: startDate || today,
        fee: parseRand(get("fee")),
        paymentPlan: template === "service" ? plan : "once_off",
        stages: plan === "phase_by_phase" ? parseStages(get("phases")) : [],
        monthlyFee: parseRand(get("monthly_fee")),
        minimumTermMonths: Number.isFinite(term) && term > 0 ? term : 0,
        retainerBilling: paid,
      },
    };
    if (!get("pdf")) problems.push('"pdf" needs the file name of the old signed PDF.');
    if (template) problems.push(...contractImportIssues(draft, nowMs).filter((p) => !problems.includes(p) && !/signing date|date it was signed|start date/i.test(p)));
    rows.push(draft);
  });

  if (rows.length === 0) return { fileProblem: "There are no contracts in that file.", rows: [] };
  return { fileProblem: "", rows };
};

/** Lower case, no punctuation, "&" as "and", single spaces: how names are compared. */
export const normaliseName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\b(pty|ltd|cc|the)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export interface MatchableClient {
  id: string;
  name: string;
  clientNumber: number | null;
}

export type ImportClientMatch =
  | { kind: "client"; client: MatchableClient; approximate: boolean }
  | { kind: "none" }
  | { kind: "unnumbered"; client: MatchableClient }
  | { kind: "ambiguous"; names: string[] };

/** True when every word of the shorter name is in the longer one ("Pondo Tours" and "Pondo Tours and Travel"). */
const sameWords = (a: string, b: string): boolean => {
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  const words = short.split(" ");
  if (short.length < 4) return false;
  const have = new Set(long.split(" "));
  return words.every((w) => have.has(w));
};

/**
 * The one client whose name is the same once punctuation and "Pty Ltd" are
 * ignored; failing that, the one client whose name holds all the same words.
 * A close match is flagged so the preview can say who it picked.
 */
export const matchImportClient = (name: string, clients: readonly MatchableClient[]): ImportClientMatch => {
  const wanted = normaliseName(name);
  if (!wanted) return { kind: "none" };
  const pick = (hits: MatchableClient[], approximate: boolean): ImportClientMatch =>
    hits.length > 1
      ? { kind: "ambiguous", names: hits.map((h) => h.name) }
      : hits[0].clientNumber === null
        ? { kind: "unnumbered", client: hits[0] }
        : { kind: "client", client: hits[0], approximate };
  const exact = clients.filter((c) => normaliseName(c.name) === wanted);
  if (exact.length > 0) return pick(exact, false);
  const close = clients.filter((c) => sameWords(normaliseName(c.name), wanted));
  return close.length > 0 ? pick(close, true) : { kind: "none" };
};

/** The job with this name (same comparison), or null. An empty name means no single job. */
export const matchImportJob = <J extends { id: string; name: string }>(name: string, jobs: readonly J[]): J | null => {
  const wanted = normaliseName(name);
  if (!wanted) return null;
  const exact = jobs.find((j) => normaliseName(j.name) === wanted);
  if (exact) return exact;
  const close = jobs.filter((j) => sameWords(normaliseName(j.name), wanted));
  return close.length === 1 ? close[0] : null;
};

/** The order to add them in: oldest signing first, so each client's references follow the signing order. */
export const importOrder = <T extends { signedOn: string; line: number }>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => a.signedOn.localeCompare(b.signedOn) || a.line - b.line);
